import { AMAZON_TAG, amazonUrl, ebayUsedUrl } from './links';
import type { Product } from './schema';

// Where a part is bought — the order's "Where" (docs/plans/2026-09-14-av-
// gear-spec.md §8.1, the typed registry). Each retailer turns a product, or a
// plain search for a part the catalogue does not carry (speaker wire), into a
// link. The list is what can be linked honestly today:
//
//   - Amazon carries the Associates tag (`roomtreatment-20`): a verified ASIN
//     goes to its product page, anything else to a tagged search. The spec's
//     launch configuration, and the one affiliate programme that is live.
//   - eBay is the used market, a search for the model (EPN not approved yet,
//     so not monetised).
//   - Best Buy is a plain search link until its programme exists (§8.2 P1).
//   - "Somewhere else" records a store with no link at all.
//
// Nothing here is ranked by commission (§8.4, and the disclosure page says
// so). A retailer whose link format cannot be confirmed is left out rather
// than guessed: a link that lands on an error page is worse than none.

export type RetailerId = 'amazon' | 'ebay' | 'bestbuy' | 'other';

export interface Retailer {
  id: RetailerId;
  label: string;
  /** A tagged affiliate link, so a purchase through it earns a commission. */
  monetised: boolean;
  /** Amazon forbids its links inside an app's WebView (§8.5): off in the app. */
  allowedInNativeApp: boolean;
  /** Where to buy `product`, or a search for `query` when there is no
   *  product (a part the catalogue does not carry); null: no link. */
  url: (target: { product: Product } | { query: string }) => string | null;
}

const q = (s: string) => encodeURIComponent(s);
const productQuery = (p: Pick<Product, 'brand' | 'model'>) => `${p.brand} ${p.model}`;

export const RETAILERS: Record<RetailerId, Retailer> = {
  amazon: {
    id: 'amazon',
    label: 'Amazon',
    monetised: true,
    allowedInNativeApp: false,
    url: (t) => {
      if ('product' in t) {
        return amazonUrl(t.product) ?? `https://www.amazon.com/s?k=${q(productQuery(t.product))}&tag=${AMAZON_TAG}`;
      }
      return `https://www.amazon.com/s?k=${q(t.query)}&tag=${AMAZON_TAG}`;
    },
  },
  ebay: {
    id: 'ebay',
    label: 'eBay (used)',
    monetised: false,
    allowedInNativeApp: true,
    url: (t) => ('product' in t ? ebayUsedUrl(t.product) : `https://www.ebay.com/sch/i.html?_nkw=${q(t.query)}`),
  },
  bestbuy: {
    id: 'bestbuy',
    label: 'Best Buy',
    monetised: false,
    allowedInNativeApp: true,
    url: (t) => `https://www.bestbuy.com/site/searchpage.jsp?st=${q('product' in t ? productQuery(t.product) : t.query)}`,
  },
  other: {
    id: 'other',
    label: 'Somewhere else',
    monetised: false,
    allowedInNativeApp: true,
    url: () => null,
  },
};

/** The order the choices are offered in: the linked stores alphabetically
 *  (never by commission, §8.4), then "Somewhere else". */
export const RETAILER_ORDER: readonly RetailerId[] = ['amazon', 'bestbuy', 'ebay', 'other'];

export function isRetailerId(v: unknown): v is RetailerId {
  return typeof v === 'string' && (RETAILER_ORDER as readonly string[]).includes(v);
}

/** Where a line is bought when the owner has not said: the affiliate store. */
export const DEFAULT_RETAILER: RetailerId = 'amazon';
